import { useEffect, useState, useRef, RefObject } from 'react';
import { Radio, Button, Input, Form, Skeleton, Empty, Alert, Select, Modal, Card, Result } from 'antd';
import { useRouter } from '@vl-core/useRouter';
import styled from '@emotion/styled';
import { RightOutlined, LeftOutlined, CalendarOutlined, UnorderedListOutlined } from '@ant-design/icons';
import useUser from '@vl-core/hooks/useUser';
import ClinicianCard from './ClinicianCard';
import DisplayAvailableSlots from './DisplayAvailableSlots';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Api } from '../../api';
import { useClinicianList } from './useClinicianList';
import { useTranslation } from 'react-i18next';
import TimezoneSelect from './TimezoneSelect';
import { BookingData } from '../../lib/context/BookingContext/bookingTypes';
import { useBookingFlowControl } from '../../hooks/useBookingFlowControl';
import { useBookingFlowData } from '../../hooks/useBookingFlowData';
import ConfirmAppointmentModal from './ConfirmAppointmentModal';
import useSystemType from 'vl-core/hooks/useSystemType';
import { SlotViewGrid, DaySlotGroup } from './SlotViewGrid';
import { DaySlotsModal } from '../Modals/Booking/DaySlotsModal';
import { SelectClinicianModal } from '../Modals/Booking/SelectClinicianModal';
import { AppointmentSlot } from 'vl-common/src/schemas/getAppointmentSlots';
import { ArrowLeftOutlined, ArrowRightOutlined } from 'antd/icons';
import { useViewportSize } from 'vl-common/src/hooks/useWindowSize';
import { useWeekChunkNavigation } from 'vl-common/src/hooks/useWeekChunkNavigation';

type ViewMode = 'slot' | 'clinician';

const ChoiceContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2px 0 40px;
  max-width: 100%;
  margin: 0 auto;

  .pagination,
  .clinician-filters {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    justify-content: space-between;
  }

  .clinician-filters * {
    max-width: 370px;
  }

  .card-box {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 1em;
    margin: 0 0 1em 0;
    width: 100%;
  }

  .card-box-item {
    position: relative;
    border: 1px solid #e8e8e8;
    background-color: #fff;
    min-height: 200px;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    overflow: auto;

    @media (min-width: 1024px) {
      flex-direction: row;
    }
  }

  .slots-overflow {
    overflow-x: auto;
    flex: 1;
    display: flex;
    flex-direction: column;
    border-top: 1px solid #e8e8e8;

    @media (min-width: 1024px) {
      border-top: none;
      border-left: 1px solid #e8e8e8;
    }
  }

  .clinician {
    @media (min-width: 1024px) {
      width: 450px;
    }
  }
`;

const HeaderContainer = styled.div`
  width: 100%;
  margin-bottom: 12px;
  text-align: left;

  h1 {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 6px 0;
    color: var(--productPrimaryDark);
  }

  p {
    font-size: 15px;
    color: var(--productPrimaryDark);
    margin: 0;
  }
`;

const ViewToggleBar = styled.div`
  display: flex;
  background-color: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 6px;
  width: 100%;
  margin-bottom: 16px;
  gap: 8px;
`;

const ViewToggleButton = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: none;
  background: transparent;
  color: var(--productPrimaryDark);
  font-weight: ${(props) => (props.active ? 'bold' : '400')};
  border-bottom: ${(props) => (props.active ? '2px solid var(--productPrimary)' : 'none')};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    font-weight: bold;
  }
`;

const SlotViewContainer = styled.div`
  width: 100%;
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 24px;

  .slot-view-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;

    h3 {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
    }
  }
`;

const SkipClinicianButton = styled.button`
  position: absolute;
  top: 6px;
  left: 6px;
  opacity: 0;
  z-index: 3;
  font-size: 18px;
  pointer-events: none;
  &:focus {
    opacity: 1;
  }
`;

const StyledSlotsBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  row-gap: 6px;
  column-gap: 12px;

  @media (min-width: 768px) {
    flex-wrap: nowrap;
    column-gap: 8px;
  }
`;

export const StyledWeekButton = styled(Button)`
  order: -1;
  flex: 1 1 calc(50% - 6px);

  @media (min-width: 768px) {
    order: 0;
    flex: 0 0 auto;
    align-self: flex-start;
  }
`;

const MOBILE_BREAKPOINT = 768;

type ChooseClinicianAndSlotProps = {
  onSuccessBooking: () => void;
};

const focusAndScrollToRedIndex = (elRefs: RefObject<(HTMLDivElement | null)[]>, indexToFocus: number): void => {
  const el = elRefs?.current[indexToFocus];
  el?.focus();
  el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

const SelectClinicianSlotAndConfirmBooking = ({ onSuccessBooking }: ChooseClinicianAndSlotProps) => {
  const { updateAppointmentClinicianSlot, resetAppointmentSlotSelection } = useBookingFlowControl();
  const { bookingData, caseData, patientData } = useBookingFlowData();
  const router = useRouter();
  const systemType = useSystemType();
  const user = useUser();
  const role = user?.user_type_code;
  const { t } = useTranslation('common');

  const isFirstAppt = bookingData?.apptType === 'NEW';
  const [viewMode, setViewMode] = useState<ViewMode>(() => (isFirstAppt ? 'slot' : 'clinician'));

  const [weekOffset, setWeekOffset] = useState(0);
  const { width } = useViewportSize();
  const isMobile = width < MOBILE_BREAKPOINT;
  const { sliceParams, isFirstChunk, goForward, goBackward } = useWeekChunkNavigation({
    isMobile,
    setWeekCount: setWeekOffset
  });

  const isFirstPage = weekOffset === 0 && isFirstChunk;

  const [selectedGridSlot, setSelectedGridSlot] = useState<AppointmentSlot | null>(null);
  const [isClinicianModalOpen, setIsClinicianModalOpen] = useState(false);

  const clinicianCardRefs: RefObject<(HTMLDivElement | null)[]> = useRef<(HTMLDivElement | null)[]>([]);

  const [slotsTimezone, setSlotsTimezone] = useState(() => user.timezone);
  const [gender, setGender] = useState('');
  const [paginationCurrent, setPaginationCurrent] = useState(1);
  const [clinicianSearchName, setClinicianSearchName] = useState('');
  const [taskMessage, setTaskMessage] = useState('');
  const [selectedAuthCancelReason, setSelectedAuthCancelReason] = useState(null);
  const [taskMessageModalVisible, setTaskMessageModalVisible] = useState(false);
  const [taskMessageConfirmModalVisible, setTaskMessageConfirmModalVisible] = useState(false);
  const [confirmAppointmentModalVisible, setConfirmAppointmentModalVisible] = useState(false);
  const [selectedMoreDayGroup, setSelectedMoreDayGroup] = useState<DaySlotGroup | null>(null);

  const getCaseId = () => {
    if (bookingData?.isNewCase) return null;
    return caseData && 'case_id' in caseData ? caseData.case_id : null;
  };

  const caseId = getCaseId();

  const {
    failureReason: removeBookingAuthorisationFailureReason,
    isError: isRemoveBookingAuthorisationError,
    isPending: isRemoveBookingAuthorisationLoading,
    mutateAsync: removeBookingAuthorisation
  } = useMutation({
    mutationFn: (data: {
      auth_cancel_code: string | null;
      case_id: number;
      user_guid: string | undefined;
      availability: string;
    }) => Api.booking.removeAuthorisation(data.user_guid, data.case_id, data.auth_cancel_code, data.availability)
  });

  const {
    data: cliniciansList = [],
    isFetching,
    isError,
    isSuccess
  } = useClinicianList({
    params: {
      subtype: bookingData?.clinType,
      specarea: caseData?.case_spec_area_code ?? '',
      spec_cat: caseData?.spec_cat_code,
      patient_guid: caseData?.patient_user_guid,
      appt_type_code: bookingData?.apptType,
      rank: String(1),
      page: paginationCurrent,
      gender,
      rows: 3,
      name: clinicianSearchName,
      case_id: caseId,
      tag_filter: true
    },
    enabled: !!caseData
  });

  const noAvailableClinicians = cliniciansList?.length === 0;
  const lastPage = cliniciansList?.length < 3;
  const isFiltersOff = !clinicianSearchName && !gender;

  const [showRequestAlternative, setShowRequestAlternative] = useState(bookingData?.bookingType !== 'BOOK');
  useEffect(() => {
    if (showRequestAlternative) return;
    setShowRequestAlternative(noAvailableClinicians && isFiltersOff && isSuccess);
  }, [cliniciansList, isFiltersOff, showRequestAlternative, noAvailableClinicians, isSuccess]);

  const { data: userAuthCancelReasons, isFetched: isUserAuthCancelReasonsFetched } = useQuery({
    queryKey: ['authCancelReasons', caseId],
    queryFn: () => Api.meta.getUsersCaseMeta(caseId),
    select: (data) => {
      const reasons: {
        auth_cancel_reason_desc: string;
        auth_cancel_reason_code: string;
      }[] = data?.data?.meta?.[0]?.user_auth_cancel_reasons || [];

      return reasons.map((reason) => ({
        label: reason.auth_cancel_reason_desc,
        value: reason.auth_cancel_reason_code
      }));
    },
    enabled: !!caseId
  });

  const onSelectClinicianSlot = ({ apptDate, clinician }: Pick<BookingData, 'apptDate' | 'clinician'>) => {
    updateAppointmentClinicianSlot({
      apptDate,
      clinician
    });
    setConfirmAppointmentModalVisible(true);
  };

  const cancelCallTypeSelection = () => {
    setConfirmAppointmentModalVisible(false);
    resetAppointmentSlotSelection();
  };

  const handleRequestAlternative = async () => {
    if (!caseId) return;

    try {
      await removeBookingAuthorisation({
        auth_cancel_code: selectedAuthCancelReason,
        case_id: caseId,
        user_guid: user?.user_guid,
        availability: taskMessage
      });
      setTaskMessageModalVisible(false);
      setTaskMessageConfirmModalVisible(true);
    } catch (error) {
      console.log('Error while removing booking authorization: ', error);
    }
  };

  const slotsDuration = cliniciansList?.[0]?.slot_duration;
  const firstFastestClinician = cliniciansList?.find((clinician) => clinician?.fastest_flag)?.clin_user_guid;
  const isAdmin = systemType === 'vl-admin';

  const onAppointmentBooked = async () => {
    setConfirmAppointmentModalVisible(false);
    onSuccessBooking();
  };

  return (
    <ChoiceContainer className="choice-container">
      <HeaderContainer>
        <h1>{t('Book an Appointment')}</h1>
        <p>
          {viewMode === 'slot'
            ? t('Pick a time that works for you and choose a clinician.')
            : t('Choose a clinician and pick a time that works for you.')}
          {slotsDuration ? ` ${t('Appointments last {{duration}} minutes', { duration: slotsDuration })}.` : ''}
        </p>
      </HeaderContainer>

      {isAdmin && <TimezoneSelect onChange={setSlotsTimezone} patientTimezone={patientData?.timezone} />}

      <ViewToggleBar data-testid="view-toggle-bar">
        <ViewToggleButton
          type="button"
          active={viewMode === 'slot'}
          onClick={() => setViewMode('slot')}
          data-testid="slot-view-btn"
        >
          <CalendarOutlined />
          {t('Slot view')}
        </ViewToggleButton>
        <ViewToggleButton
          type="button"
          active={viewMode === 'clinician'}
          onClick={() => setViewMode('clinician')}
          data-testid="clinician-view-btn"
        >
          <UnorderedListOutlined />
          {t('Clinician view')}
        </ViewToggleButton>
      </ViewToggleBar>

      {viewMode === 'clinician' && (
        <>
          <div className="clinician-filters">
            <Input.Search
              placeholder={t('Clinician Name')}
              data-testid="clinician-name-search-input"
              enterButton
              allowClear
              loading={isFetching}
              onSearch={(value) => {
                setClinicianSearchName(value);
                setPaginationCurrent(1);
              }}
            />

            <Form.Item label={t('Sex of clinician:')}>
              <Radio.Group
                onChange={(e) => {
                  setGender(e?.target?.value);
                  setPaginationCurrent(1);
                }}
                value={gender}
              >
                <Radio defaultChecked value="">
                  {t('All')}
                </Radio>
                <Radio value="M">{t('Male')}</Radio>
                <Radio value="F">{t('Female')}</Radio>
              </Radio.Group>
            </Form.Item>
          </div>

          <div className="card-box">
            {isFetching && (
              <>
                <div key="skeleton-1" className="card-box-item" style={{ padding: 20, height: 300 }}>
                  <Skeleton avatar />
                </div>
                <div key="skeleton-2" className="card-box-item" style={{ padding: 20, height: 300 }}>
                  <Skeleton avatar />
                </div>
                <div key="skeleton-3" className="card-box-item" style={{ padding: 20, height: 300 }}>
                  <Skeleton avatar />
                </div>
              </>
            )}

            {(isError || (isSuccess && noAvailableClinicians)) && (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No clinicians found')} />
            )}

            {isSuccess &&
              cliniciansList?.map((clinician, i) => {
                const hasNext = i + 1 < cliniciansList?.length;
                const hasPrevious = i - 1 >= 0;

                return (
                  <div
                    key={clinician?.clin_user_guid}
                    className="card-box-item"
                    id={`clinician-${i}`}
                    data-testid={`${clinician?.first_name || ''} ${clinician?.last_name || ''}`}
                    tabIndex={-1}
                    ref={(el) => {
                      clinicianCardRefs.current[i] = el;
                    }}
                  >
                    {hasNext && (
                      <SkipClinicianButton
                        type="button"
                        onClick={() => focusAndScrollToRedIndex(clinicianCardRefs, i + 1)}
                        data-testid="next-clinician-button"
                      >
                        {t('Skip to next clinician')}
                      </SkipClinicianButton>
                    )}
                    {hasPrevious && (
                      <SkipClinicianButton
                        type="button"
                        onClick={() => focusAndScrollToRedIndex(clinicianCardRefs, i - 1)}
                        data-testid="previous-clinician-button"
                      >
                        {t('Skip to previous clinician')}
                      </SkipClinicianButton>
                    )}
                    <div className="clinician">
                      <ClinicianCard
                        key={clinician?.clin_user_guid}
                        isFirstFastest={firstFastestClinician === clinician?.clin_user_guid}
                        clinician={clinician}
                        tags={clinician?.info_tags || []}
                        timezone={slotsTimezone}
                      />
                    </div>

                    <div className="slots-overflow">
                      <DisplayAvailableSlots
                        clinician={clinician}
                        onSelectClinicianSlot={onSelectClinicianSlot}
                        timezone={slotsTimezone}
                      />
                    </div>
                  </div>
                );
              })}

            {role === 'PATIENT' && !isFetching && showRequestAlternative && (
              <Card
                className="request-alternative-card"
                title={t('Not seeing an appointment that suits you?')}
                style={{ maxWidth: 800, margin: '0 auto' }}
                extra={
                  <Button
                    size="large"
                    onClick={() => setTaskMessageModalVisible(true)}
                    data-testid="request-alternative"
                  >
                    {t('Ask for a different appointment')}
                  </Button>
                }
              >
                {noAvailableClinicians
                  ? t(
                      "Unfortunately, we can't find any available appointments. Please ask for a different appointment."
                    )
                  : t(
                      'If you are unable to attend one of the above appointments, please request an alternative appointment.'
                    )}
              </Card>
            )}
          </div>

          <div className="pagination">
            <Button
              icon={<LeftOutlined />}
              disabled={paginationCurrent <= 1}
              onClick={() => setPaginationCurrent((val) => val - 1)}
              data-testid="previous-page"
            >
              {t('Previous')}
            </Button>

            <Button
              disabled={lastPage || noAvailableClinicians}
              onClick={() => setPaginationCurrent((val) => val + 1)}
              icon={<RightOutlined />}
              iconPosition="end"
              data-testid="next-page"
            >
              {t('Next')}
            </Button>
          </div>
        </>
      )}

      {viewMode === 'slot' && (
        <SlotViewContainer data-testid="slot-view-container">
          <div className="slot-view-header">
            <h2>{t('Available Appointments')}</h2>
          </div>
          <StyledSlotsBox>
            <StyledWeekButton
              icon={<ArrowLeftOutlined aria-hidden />}
              disabled={isFirstPage}
              onClick={goBackward}
              aria-label={t('Previous week')}
              data-testid="slot-view-prev-week"
            />

            <SlotViewGrid
              patientGuid={caseData?.patient_user_guid ?? undefined}
              weekOffset={weekOffset}
              sliceParams={sliceParams}
              clinSubtype={bookingData?.clinType ?? undefined}
              clinSpeccat={caseData?.spec_cat_code ?? undefined}
              clinSpecarea={caseData?.case_spec_area_code ?? undefined}
              apptTypeCode={bookingData?.apptType ?? undefined}
              onSelectSlot={(slot) => {
                setSelectedGridSlot(slot);
                setIsClinicianModalOpen(true);
              }}
              onShowMoreForDay={(dayGroup) => {
                setSelectedMoreDayGroup(dayGroup);
              }}
            />
            <StyledWeekButton
              icon={<ArrowRightOutlined aria-hidden />}
              aria-label={t('Next week')}
              onClick={goForward}
              data-testid="slot-view-next-week"
            />
          </StyledSlotsBox>

          <DaySlotsModal
            open={Boolean(selectedMoreDayGroup)}
            dayGroup={selectedMoreDayGroup}
            onClose={() => setSelectedMoreDayGroup(null)}
            onSelectSlot={(slot) => {
              setSelectedGridSlot(slot);
              setIsClinicianModalOpen(true);
              setSelectedMoreDayGroup(null);
            }}
          />

          <SelectClinicianModal
            open={isClinicianModalOpen}
            onCancel={() => {
              setIsClinicianModalOpen(false);
              setSelectedGridSlot(null);
            }}
            selectedSlot={selectedGridSlot}
            patientGuid={caseData?.patient_user_guid ?? undefined}
            clinSubtype={bookingData?.clinType ?? undefined}
            clinSpeccat={caseData?.spec_cat_code ?? undefined}
            clinSpecarea={caseData?.case_spec_area_code ?? undefined}
            apptTypeCode={bookingData?.apptType ?? undefined}
            caseId={caseId}
            onSelectClinicianSlot={onSelectClinicianSlot}
          />
        </SlotViewContainer>
      )}

      <ConfirmAppointmentModal
        isVisible={confirmAppointmentModalVisible}
        onSuccessBooking={onAppointmentBooked}
        onModalClose={cancelCallTypeSelection}
      />

      <Modal
        title={t('Asking for a different appointment')}
        centered
        open={taskMessageModalVisible}
        onCancel={() => setTaskMessageModalVisible(false)}
        okText="Request"
        okButtonProps={{ loading: isRemoveBookingAuthorisationLoading, disabled: !taskMessage }}
        onOk={handleRequestAlternative}
      >
        <div style={{ marginBottom: '16px' }}>
          {t(
            "Please tell us why you need a different clinician. Please also tell us when you're available for an appointment during the next three weeks."
          )}
        </div>
        {isRemoveBookingAuthorisationError && (
          <Alert
            className="login-alert"
            message={removeBookingAuthorisationFailureReason?.message || 'Unable to request alternative appointment.'}
            type="error"
            showIcon
          />
        )}
        <Form layout="vertical">
          <Form.Item label={t('Message')} htmlFor="taskMessage">
            <Input.TextArea
              name="taskMessage"
              rows={6}
              value={taskMessage}
              onChange={(e) => setTaskMessage(e.target.value)}
            />
          </Form.Item>

          {isUserAuthCancelReasonsFetched && (
            <Form.Item label={t('Reason for request')} htmlFor="taskReason">
              <Select
                options={userAuthCancelReasons}
                value={selectedAuthCancelReason}
                onChange={setSelectedAuthCancelReason}
                data-testid="auth-cancel-reason-select"
              />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal centered open={taskMessageConfirmModalVisible} footer={null} closable={false}>
        <Result
          status="success"
          title="Alternative appointment requested"
          subTitle="Our contact centre has been notified and will be in touch soon to arrange an alternative appointment."
          extra={[
            <Button
              key="submit"
              onClick={() => {
                setTaskMessageConfirmModalVisible(false);
                router.push('/patients');
              }}
              data-testid="return-to-dashboard"
            >
              {t('Return to dashboard')}
            </Button>
          ]}
        />
      </Modal>
    </ChoiceContainer>
  );
};

export default SelectClinicianSlotAndConfirmBooking;
